// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslate } from '@plesk/ui-library';
import type { Sticker as StickerItem } from '../registry';
import { getPosition } from './position';
import { burstStar, burstParticles } from './burst';

import COLLECT_STICKER from './CollectSticker.graphql';

interface StickerProps {
    sticker: StickerItem;
    collected: boolean;
}

const Sticker = ({ sticker, collected }: StickerProps) => {
    const [justCollected, setJustCollected] = useState(false);
    const [hidden, setHidden] = useState(collected);
    const [collectSticker] = useMutation(COLLECT_STICKER);
    const translate = useTranslate('components.layouts.StickerCollectionGame');

    const handleCollect = () => {
        setJustCollected(true);
        collectSticker({ variables: { input: { stickerId: sticker.id } } });
    };

    useEffect(() => {
        if (!justCollected) {
            return undefined;
        }
        const timer = setTimeout(() => setHidden(true), 2000);
        return () => clearTimeout(timer);
    }, [justCollected]);

    if (hidden) {
        return null;
    }

    const { top, left } = getPosition(sticker.id);

    const tooltip = translate('collect', { sticker: translate(`Sticker.${sticker.id}`) }) as string;

    return (
        <button
            data-action="collect-sticker"
            type="button"
            className="sticker-collection-layer__item"
            style={{ top: `calc((100vh - 64px) * ${top / 100})`, left: `calc((100vw - 64px) * ${left / 100})` }}
            onClick={handleCollect}
        >
            {justCollected ? (
                <span className="sticker-collection-layer__burst" aria-hidden="true">
                    <img className="sticker-collection-layer__burst-star" src={burstStar()} alt="" />
                    {burstParticles.map(particle => (
                        <img
                            key={`${particle.left}-${particle.top}`}
                            className="sticker-collection-layer__burst-particle"
                            src={particle.image()}
                            alt=""
                            style={{
                                left: `${particle.left}%`,
                                top: `${particle.top}%`,
                                width: `${particle.width}%`,
                                animationDelay: `${particle.delay}s`,
                            }}
                        />
                    ))}
                </span>
            ) : (
                <>
                    <span className="sticker-collection-layer__frame">
                        <img src={sticker.image()} alt={tooltip} />
                    </span>
                    <span className="sticker-collection-layer__tooltip" aria-hidden="true">{tooltip}</span>
                </>
            )}
        </button>
    );
};

export default Sticker;
